import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  MarkEmailReadRounded as VerifiedIcon,
  SendRounded as ResendIcon,
  CheckCircleOutlineRounded as SuccessIcon,
  ErrorOutlineRounded as ErrorIcon,
} from '@mui/icons-material';
import { THEME_SHADOWS, COMMON_STYLES, THEME_RADIUS, THEME_COLORS } from '../constants/theme';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import { consumeAuthNext, peekAuthNext, rememberAuthNext, sanitizeNextPath } from '../utils/authNext';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import { documentPageCanvasSx } from '../constants/documentTheme';
import useAuthPageBranding from '../hooks/useAuthPageBranding';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useAuth();
  const { brandName, brandColor } = useAuthPageBranding();

  // Get email and token from URL/state
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const nextFromQuery = sanitizeNextPath(searchParams.get('next'));
  const emailFromState = location.state?.email || '';
  const nextFromState = sanitizeNextPath(location.state?.next);

  const [status, setStatus] = useState(token ? 'verifying' : 'pending'); // verifying | pending | success | error | expired
  const [error, setError] = useState('');
  const [email] = useState(emailFromState);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    rememberAuthNext(nextFromQuery || nextFromState);
  }, [nextFromQuery, nextFromState]);

  // Auto-verify if token is present in URL
  const verifyToken = useCallback(async (verificationToken) => {
    setStatus('verifying');
    try {
      const response = await api.get(`/auth/verify-email?token=${verificationToken}`);
      
      if (response.data.success) {
        setStatus('success');
        
        // Auto-login: store the token and redirect
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          const destination = consumeAuthNext(nextFromQuery || nextFromState) || '/trips';
          // Short delay so user sees the success message
          setTimeout(() => {
            window.location.href = destination;
          }, 2000);
        }
      }
    } catch (err) {
      console.error('Verification error:', err);
      if (err.response?.data?.expired) {
        setStatus('expired');
        setError('Your verification link has expired. Please request a new one.');
      } else {
        setStatus('error');
        setError(err.response?.data?.error || 'Verification failed. The link may be invalid or expired.');
      }
    }
  }, [nextFromQuery, nextFromState]);

  useEffect(() => {
    if (token) {
      verifyToken(token);
    }
  }, [token, verifyToken]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleResend = async () => {
    if (!email || resending || cooldown > 0) return;

    setResending(true);
    setResendSuccess(false);
    setError('');

    try {
      const response = await api.post('/auth/resend-verification', {
        email,
        next: peekAuthNext() || nextFromQuery || nextFromState || undefined,
      });
      
      if (response.data.alreadyVerified) {
        setStatus('success');
        setTimeout(() => {
          const destination = consumeAuthNext(nextFromQuery || nextFromState) || '/login';
          navigate(destination);
        }, 2000);
      } else {
        setResendSuccess(true);
        setCooldown(60); // 60 second cooldown
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend verification email.');
    } finally {
      setResending(false);
    }
  };

  // Verifying state
  if (status === 'verifying') {
    return (
      <Box sx={documentPageCanvasSx}>
        <SEOHead title={brandName ? `Verifying Email - ${brandName}` : 'Verifying Email - Déjà Vu Nomad'} noIndex={true} />
        <DocumentMarketingHero title="Verifying Your Email..." />
        <Container maxWidth="sm" sx={{ py: { xs: 3, md: 4 } }}>
          <Paper elevation={0} sx={{ ...COMMON_STYLES.professionalCard, p: { xs: 4, md: 5 }, boxShadow: THEME_SHADOWS.heavy, textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ color: brandColor, mb: 3 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              Please wait while we verify your email...
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <Box sx={documentPageCanvasSx}>
        <SEOHead title={brandName ? `Email Verified - ${brandName}` : 'Email Verified - Déjà Vu Nomad'} noIndex={true} />
        <DocumentMarketingHero title="Email Verified!" />
        <Container maxWidth="sm" sx={{ py: { xs: 3, md: 4 } }}>
          <Paper elevation={0} sx={{ ...COMMON_STYLES.professionalCard, p: { xs: 4, md: 5 }, boxShadow: THEME_SHADOWS.heavy, textAlign: 'center' }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 3,
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
            }}>
              <SuccessIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
              {brandName ? `Welcome to ${brandName}!` : 'Welcome to Déjà Vu Nomad!'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your email has been successfully verified. Redirecting you...
            </Typography>
            <CircularProgress size={24} sx={{ color: brandColor }} />
          </Paper>
        </Container>
      </Box>
    );
  }

  // Pending / Error / Expired state (check your email page)
  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead title={brandName ? `Check Your Email - ${brandName}` : 'Check Your Email - Déjà Vu Nomad'} noIndex={true} />
      <DocumentMarketingHero
        eyebrow="Almost there"
        title="Check Your Email"
        subtitle="We sent a verification link to activate your account"
      />

      {/* Content */}
      <Container maxWidth="sm" sx={{ py: { xs: 3, md: 4 } }}>
        <Paper elevation={0} sx={{ ...COMMON_STYLES.professionalCard, p: { xs: 4, md: 5 }, boxShadow: THEME_SHADOWS.heavy }}>
          
          {/* Icon */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              background: status === 'error' || status === 'expired'
                ? 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)'
                : (brandName ? brandColor : 'linear-gradient(135deg, #591716 0%, #7a1f1e 100%)'),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto',
              boxShadow: status === 'error' || status === 'expired'
                ? '0 4px 12px rgba(255, 152, 0, 0.3)'
                : '0 4px 12px rgba(89, 23, 22, 0.3)',
            }}>
              {status === 'error' || status === 'expired' ? (
                <ErrorIcon sx={{ fontSize: 40, color: 'white' }} />
              ) : (
                <VerifiedIcon sx={{ fontSize: 40, color: 'white' }} />
              )}
            </Box>
          </Box>

          {/* Status message */}
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center', color: 'text.primary' }}>
            {status === 'expired' ? 'Link Expired' : status === 'error' ? 'Verification Failed' : 'Verify Your Email'}
          </Typography>

          {email && status === 'pending' && (
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
              We've sent a verification link to{' '}
              <strong style={{ color: '#1a1a1a' }}>{email}</strong>.
              Click the link in the email to activate your account.
            </Typography>
          )}

          {(status === 'error' || status === 'expired') && (
            <Alert 
              severity={status === 'expired' ? 'warning' : 'error'} 
              sx={{ mb: 3, borderRadius: THEME_RADIUS.small }}
            >
              {error}
            </Alert>
          )}

          {resendSuccess && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: THEME_RADIUS.small }}>
              Verification email sent! Please check your inbox (and spam folder).
            </Alert>
          )}

          {/* Instructions */}
          {status === 'pending' && (
            <Box sx={{
              backgroundColor: '#f8f9fa', borderRadius: 2, p: 2.5, mb: 3,
              border: '1px solid #e0e0e0',
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                What to do:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                1. Open your email inbox<br />
                2. Look for an email from <strong>{brandName || 'Déjà Vu Nomad'}</strong><br />
                3. Click the <strong>"Verify My Email"</strong> button<br />
                4. You'll be automatically signed in
              </Typography>
            </Box>
          )}

          {/* Resend button */}
          {email && (
            <Button
              fullWidth
              variant={status === 'pending' ? 'outlined' : 'contained'}
              size="large"
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              startIcon={resending ? <CircularProgress size={18} /> : <ResendIcon />}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                mb: 2,
                borderRadius: THEME_RADIUS.small,
                textTransform: 'none',
                fontWeight: 600,
                ...(status !== 'pending' && {
                  backgroundColor: THEME_COLORS.primary.main,
                  '&:hover': { backgroundColor: THEME_COLORS.primary.dark },
                }),
              }}
            >
              {resending 
                ? 'Sending...' 
                : cooldown > 0 
                  ? `Resend in ${cooldown}s` 
                  : 'Resend Verification Email'}
            </Button>
          )}

          {/* Back to login */}
          <Button
            fullWidth
            variant="text"
            onClick={() => navigate('/login')}
            sx={{
              py: 1.5,
              fontSize: '0.95rem',
              textTransform: 'none',
              fontWeight: 600,
              color: THEME_COLORS.primary.main,
            }}
          >
            Back to Sign In
          </Button>

          {/* Spam notice */}
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            Didn't receive the email? Check your spam or junk folder.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyEmail;
